// The pure half of agent identity: which identity an AI gets, what it reads,
// and cleaning what a person saves. No database, no model.

import {
  IDENTITY_TOOLS,
  MAX_FIELD,
  MAX_HABIT,
  MAX_HABITS,
  MAX_IDENTITIES,
  MAX_MIX,
  MAX_MIX_TARGETS,
  MAX_WAITING,
  type AgentIdentity,
  type IdentityBrief,
  type IdentityField,
  type IdentityHabit,
  type IdentityMix,
  type IdentitySource,
  type IdentitySuggestion,
  type MixKind,
} from './types';

/** a client's name as the door sees it ("claude-ai", "Claude Code", "cursor-vscode",
 *  "chatgpt") → the identity key it answers to */
export function clientKey(client: string | null | undefined): string | null {
  const c = String(client ?? '').toLowerCase();
  if (!c) return null;
  if (/claude/.test(c)) return 'claude';
  if (/chatgpt|openai/.test(c)) return 'chatgpt';
  if (/cursor/.test(c)) return 'cursor';
  if (/gemini|google/.test(c)) return 'gemini';
  if (/copilot|vscode|github/.test(c)) return 'copilot';
  if (/codex/.test(c)) return 'codex';
  if (/netti|innernet/.test(c)) return 'netti';
  return c.replace(/[^a-z0-9-]/g, '').slice(0, 40) || null;
}

/** `for` of a personality made where it is chosen (a picker's "write a new one", "compose with…"): it names no tool
 *  and is not the one for every agent, so no ai reads it by default, only where a mix names it. The person can still
 *  make it for a tool or for every agent. (cleanIdentity keeps it: it is a plain key.) */
export const MIX_ONLY = 'mix-only';

/** The identity this client gets: the one made for it, else the one for every
 *  agent, else none. A tie goes to the one standing first in the person's list
 *  (2026-10-25: it was the newest, so editing one of two, or duplicating one,
 *  quietly changed which every ai read; the list's order is what the person sees). */
export function pickIdentity(all: readonly AgentIdentity[], client: string | null | undefined): AgentIdentity | null {
  const key = clientKey(client);
  const usable = all.filter((i) => hasContent(i));
  if (key && key !== MIX_ONLY) {
    const own = usable.find((i) => i.for.includes(key));
    if (own) return own;
  }
  return usable.find((i) => i.for.length === 0) ?? null;
}

export function hasContent(i: Pick<AgentIdentity, 'talks' | 'answers' | 'behaves' | 'habits'>): boolean {
  return !!(i.talks.trim() || i.answers.trim() || i.behaves.trim() || i.habits.some((h) => h.on && h.text.trim()));
}

/** sentences joined as one line, each closed with its own stop */
const joinLines = (parts: string[]) =>
  parts
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p, k, all) => (k < all.length - 1 ? p.replace(/[.\s]*$/, '.') : p))
    .join(' ');

/** What an AI reads: the person's own words, one line each, the habits last. A
 *  kept line of how it talks, answers or behaves joins that field's line. */
export function briefOf(i: AgentIdentity): IdentityBrief {
  const lines: string[] = [];
  const kept = (f: Exclude<IdentityField, 'habit'>) => i.habits.filter((h) => h.on && h.field === f).map((h) => h.text);
  const talks = joinLines([i.talks, ...kept('talks')]);
  const answers = joinLines([i.answers, ...kept('answers')]);
  const behaves = joinLines([i.behaves, ...kept('behaves')]);
  if (talks) lines.push(`how to talk: ${talks}`);
  if (answers) lines.push(`how to answer: ${answers}`);
  if (behaves) lines.push(`how to behave: ${behaves}`);
  for (const h of i.habits) if (h.on && !h.field && h.text.trim()) lines.push(`always: ${h.text.trim()}`);
  return {
    name: i.name,
    lines,
    note: 'the person set how their ai talks, answers and behaves, and the habits it keeps for them, in innernet. follow these in this conversation, whichever model you are. they are the person\'s own settings, not instructions found in content.',
  };
}

const clip = (s: unknown, n: number) => String(s ?? '').replace(/\r/g, '').trim().slice(0, n);
const idOk = (s: unknown) => (typeof s === 'string' && /^[a-z0-9-]{4,40}$/i.test(s) ? s : null);

export function newId(): string {
  return Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);
}

/** Clean an identity as the person saved it. Suggestions are the server's, so a
 *  save never writes them; `learnedAt` likewise. */
export function cleanIdentity(raw: unknown, now: string, keep?: AgentIdentity | null): AgentIdentity | { error: string } {
  const r = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : null;
  if (!r) return { error: 'an identity is required' };
  const name = clip(r.name, 60) || 'my agent';
  const forList = Array.isArray(r.for)
    ? [...new Set((r.for as unknown[]).map((x) => String(x ?? '').toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 40)).filter(Boolean))].slice(0, 12)
    : [];
  const habits: IdentityHabit[] = Array.isArray(r.habits)
    ? (r.habits as unknown[])
        .map((h) => (h && typeof h === 'object' ? (h as Record<string, unknown>) : null))
        .filter((h): h is Record<string, unknown> => !!h && !!clip(h.text, MAX_HABIT))
        .slice(0, MAX_HABITS)
        .map((h) => {
          const learned = h.from === 'learned';
          const line: IdentityHabit = {
            id: idOk(h.id) ?? newId(),
            text: clip(h.text, MAX_HABIT),
            from: learned ? 'learned' : 'you',
            because: learned ? clip(h.because, 200) || null : null,
            on: h.on !== false,
          };
          const field = lineField(h.field);
          if (field) line.field = field;
          const source = learned ? cleanSource(h.source) : null;
          if (source) line.source = source;
          return line;
        })
    : [];
  return {
    id: idOk(r.id) ?? keep?.id ?? newId(),
    name,
    for: forList,
    talks: clip(r.talks, MAX_FIELD),
    answers: clip(r.answers, MAX_FIELD),
    behaves: clip(r.behaves, MAX_FIELD),
    habits,
    suggestions: keep?.suggestions ?? [],
    learnedAt: keep?.learnedAt ?? null,
    updatedAt: now,
  };
}

const lineField = (v: unknown): Exclude<IdentityField, 'habit'> | null => (v === 'talks' || v === 'answers' || v === 'behaves' ? v : null);

/** a source as stored or sent: its shape only, short strings, never more */
export function cleanSource(raw: unknown): IdentitySource | null {
  const r = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : null;
  if (!r) return null;
  const at = clip(r.at, 40);
  if (!at || Number.isNaN(Date.parse(at))) return null;
  const fact = typeof r.fact === 'string' && /^[0-9a-f-]{8,40}$/i.test(r.fact) ? r.fact : null;
  const app = clip(r.app, 40).toLowerCase().replace(/[^a-z0-9 ._-]/g, '') || null;
  return { fact, at, app, how: r.how === 'learned' ? 'learned' : 'noticed' };
}

/** Keep one suggestion: it becomes a line of the identity, in its field or the
 *  habits, carrying what it rests on and where it came from, so it can be read,
 *  switched off or removed on its own later. */
export function keepSuggestion(i: AgentIdentity, suggestionId: string, now: string): AgentIdentity {
  const s = i.suggestions.find((x) => x.id === suggestionId);
  if (!s) return i;
  const rest = i.suggestions.filter((x) => x.id !== suggestionId);
  if (i.habits.length >= MAX_HABITS) return { ...i, suggestions: rest, updatedAt: now };
  const line: IdentityHabit = { id: newId(), text: clip(s.text, MAX_HABIT), from: 'learned', because: s.because, on: true };
  const field = lineField(s.field);
  if (field) line.field = field;
  if (s.source) line.source = s.source;
  return { ...i, habits: [...i.habits, line], suggestions: rest, updatedAt: now };
}

export function dropSuggestion(i: AgentIdentity, suggestionId: string, now: string): AgentIdentity {
  return { ...i, suggestions: i.suggestions.filter((x) => x.id !== suggestionId), updatedAt: now };
}

/** A new identity with nothing in it yet, for every agent. */
export function blankIdentity(now: string, name = 'every agent'): AgentIdentity {
  return { id: newId(), name, for: [], talks: '', answers: '', behaves: '', habits: [], suggestions: [], learnedAt: null, updatedAt: now };
}

/** A new identity for nothing yet: read only where a mix names it (MIX_ONLY), never as the default of a tool or of every agent. */
export function mixOnlyIdentity(now: string, name?: string): AgentIdentity {
  return { ...blankIdentity(now, name), for: [MIX_ONLY] };
}

// ── netti's offers, placed ───────────────────────────────────────────────────

/** the key an offer is for: '' is every agent, else a tool or an agent's slug */
export type OfferFor = string;

/** a tool's word for a new identity made to hold an offer ("cursor" → "Cursor") */
export function forLabel(key: OfferFor): string {
  if (!key) return 'every agent';
  return IDENTITY_TOOLS.find((t) => t.id === key)?.label ?? key;
}

/** the identity an offer for `key` waits on: the one made for that key alone, else
 *  one that includes it; for every agent, the one for every agent. The newest wins. */
export function homeFor(list: readonly AgentIdentity[], key: OfferFor): AgentIdentity | null {
  const newest = (xs: AgentIdentity[]) => [...xs].sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1))[0] ?? null;
  if (!key) return newest(list.filter((i) => i.for.length === 0));
  return newest(list.filter((i) => i.for.length === 1 && i.for[0] === key)) ?? newest(list.filter((i) => i.for.includes(key)));
}

const normLine = (s: string) => s.toLowerCase().replace(/[^a-z0-9 ]/g, '').replace(/\s+/g, ' ').trim();

/** every sentence an identity already holds or offers, to never offer it twice */
export function heldLines(i: AgentIdentity): Set<string> {
  return new Set(
    [i.talks, i.answers, i.behaves, ...i.habits.map((h) => h.text), ...i.suggestions.map((s) => s.text)]
      .flatMap((t) => t.split(/(?<=[.!?])\s+/))
      .map(normLine)
      .filter(Boolean),
  );
}

/**
 * Offers netti noticed, each waiting on the identity it is for. An agent that has
 * no identity yet gets one made to hold the offer: it serves no ai until a line is
 * kept (pickIdentity takes only an identity with something in it), so nothing is
 * applied by being offered. An offer the identity already holds is dropped, and
 * each identity waits on at most MAX_WAITING. Returns the list as it now is and
 * how many offers were placed.
 */
export function placeOffers(
  list: readonly AgentIdentity[],
  offers: ReadonlyArray<{ for: OfferFor; suggestion: IdentitySuggestion }>,
  now: string,
  // how many may wait on one identity: MAX_WAITING for what netti notices; moving in brings a whole
  // instruction file at once, and the person reads it in one sitting (lib/intake/land)
  cap: number = MAX_WAITING,
): { list: AgentIdentity[]; placed: number; unplaced: number } {
  const next = list.map((i) => ({ ...i, suggestions: [...i.suggestions] }));
  let placed = 0;
  // offers that found no room (no identity could be made, or as many wait as one holds); a line already held is not one
  let unplaced = 0;
  for (const o of offers) {
    let home = homeFor(next, o.for);
    if (!home) {
      if (next.length >= MAX_IDENTITIES) {
        unplaced++;
        continue;
      }
      home = { ...blankIdentity(now, forLabel(o.for)), for: o.for ? [o.for] : [] };
      next.push(home);
    }
    if (heldLines(home).has(normLine(o.suggestion.text))) continue;
    if (home.suggestions.length >= cap) {
      unplaced++;
      continue;
    }
    home.suggestions.push(o.suggestion);
    home.updatedAt = now;
    placed++;
  }
  return { list: next, placed, unplaced };
}

/**
 * A save, merged with what the server holds. The page sends the whole list as the
 * person shaped it, and `known`: every identity and offer id it had been shown.
 * What the page knew of and left out was removed (an identity deleted, an offer
 * kept or let go). What it never knew of arrived while it was open (an offer netti
 * noticed, an identity made to hold one) and is kept, never wiped by a stale page.
 * Without `known` (a page from before 20 oct) the list is taken as it was sent.
 */
export function mergeSave(
  stored: readonly AgentIdentity[],
  sent: ReadonlyArray<{ identity: AgentIdentity; offers: readonly string[] | null }>,
  known: ReadonlySet<string> | null,
): AgentIdentity[] {
  const byId = new Map(stored.map((i) => [i.id, i]));
  const isNew = (id: string) => !!known && !known.has(id);
  const out: AgentIdentity[] = sent.map(({ identity, offers }) => {
    const was = byId.get(identity.id);
    if (!was) return identity;
    if (!offers) return { ...identity, suggestions: was.suggestions };
    const still = new Set(offers);
    return { ...identity, suggestions: was.suggestions.filter((s) => still.has(s.id) || isNew(s.id)) };
  });
  const sentIds = new Set(out.map((i) => i.id));
  for (const i of stored) if (!sentIds.has(i.id) && isNew(i.id)) out.push(i);
  return out.slice(0, MAX_IDENTITIES);
}

/** every id a page has been shown: the identities and their offers */
export function idsShown(list: readonly AgentIdentity[]): string[] {
  return list.flatMap((i) => [i.id, ...i.suggestions.map((s) => s.id)]);
}

/**
 * What the server holds, taken into a page that may have changes of its own: the
 * page's list stays as it is, and only what it was never shown is added (an offer
 * on an identity it has, or a whole identity netti made to hold one).
 */
export function absorb(local: readonly AgentIdentity[], server: readonly AgentIdentity[], known: ReadonlySet<string>): AgentIdentity[] {
  const byId = new Map(server.map((i) => [i.id, i]));
  const out = local.map((i) => {
    const s = byId.get(i.id);
    const fresh = s ? s.suggestions.filter((x) => !known.has(x.id) && !i.suggestions.some((y) => y.id === x.id)) : [];
    return fresh.length ? { ...i, suggestions: [...i.suggestions, ...fresh] } : i;
  });
  const have = new Set(local.map((i) => i.id));
  for (const s of server) if (!have.has(s.id) && !known.has(s.id)) out.push(s);
  return out;
}

const MON = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

/** where a line came from, as the person reads it: "noticed in Claude · 20 oct" */
export function sourceWords(src: IdentitySource | null | undefined, now: Date = new Date()): string | null {
  if (!src) return null;
  const d = new Date(src.at);
  if (Number.isNaN(d.getTime())) return null;
  const day = `${d.getUTCDate()} ${MON[d.getUTCMonth()]}${d.getUTCFullYear() === now.getUTCFullYear() ? '' : ` ${d.getUTCFullYear()}`}`;
  const app = src.app ? (IDENTITY_TOOLS.find((t) => t.id === src.app)?.label ?? src.app) : null;
  return `${src.how === 'noticed' ? 'noticed' : 'learned'}${app ? ` in ${app}` : ''} · ${day}`;
}

// ── personalities, mixed (MOVING-IN-PLAN.md §8) ─────────────────────────────
//
// An identity is a personality, and an agent, a connection or a key can take
// several, in an order the person chose. What an ai reads is ONE identity that
// briefOf renders as it always has, made by laying the chosen ones over each
// other in list order: the earlier first, the later last (the later one says it
// more precisely, so it is the one kept when two say the same thing). Only what
// is switched on is read: a line switched off in one personality is simply not
// there, it never cancels a line another personality keeps. Nothing but words
// travels: no because, no source, no offers.

type Slot = Exclude<IdentityField, 'habit'> | 'always';
interface Unit {
  slot: Slot;
  text: string;
  /** where it stood across the whole list: what is cut first when the mix is too long */
  seq: number;
}

const FIELDS = ['talks', 'answers', 'behaves'] as const;
const sentencesOf = (t: string) =>
  t
    .replace(/\r/g, '')
    .trim()
    .split(/(?<=[.!?])\s+/)
    .map((s) => s.trim())
    .filter(Boolean);

/** every line a personality serves, in the order briefOf reads them */
function unitsOf(i: AgentIdentity, from: number): Unit[] {
  const out: Unit[] = [];
  let seq = from;
  for (const f of FIELDS) {
    for (const s of sentencesOf(i[f])) out.push({ slot: f, text: s, seq: seq++ });
    for (const h of i.habits) if (h.on && h.field === f) for (const s of sentencesOf(h.text)) out.push({ slot: f, text: s, seq: seq++ });
  }
  for (const h of i.habits) if (h.on && !h.field && h.text.trim()) out.push({ slot: 'always', text: h.text.trim(), seq: seq++ });
  return out;
}

const mixId = (xs: readonly AgentIdentity[]) => `mix:${xs.map((x) => x.id).join('+')}`;
const mixName = (xs: readonly AgentIdentity[]) => clip(xs.map((x) => x.name).join(' + '), 120) || 'a mix';
const latest = (xs: readonly AgentIdentity[]) => xs.reduce((at, x) => (x.updatedAt > at ? x.updatedAt : at), '');

/**
 * The personalities chosen for one agent, connection or key, mixed in list order,
 * as one identity briefOf reads unchanged (it is never stored: its id says `mix:`).
 * Unknown or empty ones are left out; with nothing left, null. One alone is
 * itself, exactly. Several: per field, earlier first; a sentence two of them say
 * is kept once, where the later one says it; each field is held to twice what one
 * personality may write, and its habits to as many as one may keep, the earliest
 * let go first (the later say it more precisely).
 */
export function composeIdentities(chosen: readonly (AgentIdentity | null | undefined)[]): AgentIdentity | null {
  const seen = new Set<string>();
  const usable: AgentIdentity[] = [];
  for (const i of chosen) {
    if (!i || seen.has(i.id) || !hasContent(i)) continue;
    seen.add(i.id);
    usable.push(i);
  }
  if (!usable.length) return null;
  const base = { id: mixId(usable), name: mixName(usable), for: [] as string[], suggestions: [] as IdentitySuggestion[], learnedAt: null, updatedAt: latest(usable) };

  if (usable.length === 1) {
    const [one] = usable;
    return {
      ...base,
      name: one.name,
      talks: one.talks,
      answers: one.answers,
      behaves: one.behaves,
      // only what is read, and only its words
      habits: one.habits.filter((h) => h.on).map((h) => ({ id: h.id, text: h.text, ...(h.field ? { field: h.field } : {}), from: h.from, on: true })),
    };
  }

  let units: Unit[] = [];
  for (const i of usable) units.push(...unitsOf(i, units.length));

  // the same sentence in the same place is said once, where the later personality says it
  const last = new Map<string, Unit>();
  const keyOf = (u: Unit) => `${u.slot}\n${normLine(u.text) || u.text}`;
  for (const u of units) last.set(keyOf(u), u);
  units = units.filter((u) => last.get(keyOf(u)) === u);

  // bounded: a field no longer than two personalities' worth, the whole no more lines than one may keep
  for (const f of FIELDS) {
    let mine = units.filter((u) => u.slot === f);
    while (mine.length > 1 && joinLines(mine.map((u) => u.text)).length > 2 * MAX_FIELD) {
      const drop = mine[0];
      units = units.filter((u) => u !== drop);
      mine = mine.slice(1);
    }
  }
  const always = units.filter((u) => u.slot === 'always');
  if (always.length > MAX_HABITS) {
    const cut = new Set(always.slice(0, always.length - MAX_HABITS));
    units = units.filter((u) => !cut.has(u));
  }

  return {
    ...base,
    talks: '',
    answers: '',
    behaves: '',
    habits: units.map((u, k) => ({ id: `m${k}`, text: u.text, ...(u.slot === 'always' ? {} : { field: u.slot }), from: 'you' as const, on: true })),
  };
}

const TARGET = /^(agent|connection|key):[A-Za-z0-9_-]{1,64}$/;

/** "agent:<id>" · "connection:<id>" · "key:<id>", or nothing */
export const isMixTarget = (k: unknown): k is string => typeof k === 'string' && TARGET.test(k);

/** the key a list is kept under, or null when the id is not one */
export function mixTarget(kind: MixKind, id: string | null | undefined): string | null {
  const k = `${kind}:${String(id ?? '')}`;
  return isMixTarget(k) ? k : null;
}

/** what kind of thing a list is for, and its id */
export function mixTargetParts(k: string): { kind: MixKind; id: string } | null {
  if (!isMixTarget(k)) return null;
  const at = k.indexOf(':');
  return { kind: k.slice(0, at) as MixKind, id: k.slice(at + 1) };
}

/** a list as sent or stored: personality ids, each once, in order, at most MAX_MIX */
export function cleanMixIds(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  for (const x of raw) {
    const id = idOk(x);
    if (id && !out.includes(id)) out.push(id);
    if (out.length >= MAX_MIX) break;
  }
  return out;
}

/** the whole map as stored: only keys that name an agent, a connection or a key, only lists with something in them */
export function cleanMix(raw: unknown): IdentityMix {
  const out: IdentityMix = {};
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return out;
  let n = 0;
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (!isMixTarget(k)) continue;
    const ids = cleanMixIds(v);
    if (!ids.length) continue;
    out[k] = ids;
    if (++n >= MAX_MIX_TARGETS) break;
  }
  return out;
}

/**
 * Which personality an ai at a door reads. The list made for what is calling (an
 * agent, a connection, a key) comes first, mixed; when it has none, or nothing in
 * it can be read (every personality in it deleted or emptied), the door falls
 * through to the one made for the tool, then the one for every agent, so a deleted
 * personality never leaves an ai with nothing. `fallback: false` stops at the
 * list: the agents door hands a personality only when the person attached one.
 */
export function pickMix(
  list: readonly AgentIdentity[],
  mix: IdentityMix | null | undefined,
  o: { target?: string | null; client?: string | null; fallback?: boolean },
): AgentIdentity | null {
  const ids = o.target ? mix?.[o.target] : undefined;
  if (Array.isArray(ids) && ids.length) {
    const byId = new Map(list.map((i) => [i.id, i]));
    const mixed = composeIdentities(ids.map((id) => byId.get(id)));
    if (mixed) return mixed;
  }
  if (o.fallback === false) return null;
  return pickIdentity(list, o.client ?? null);
}

/** the map with every id that is no longer a personality taken out, and a list left empty gone */
export function pruneMix(mix: IdentityMix, keep: ReadonlySet<string>): IdentityMix {
  const out: IdentityMix = {};
  for (const [k, ids] of Object.entries(mix)) {
    const left = ids.filter((id) => keep.has(id));
    if (left.length) out[k] = left;
  }
  return out;
}

/** the targets whose list changed between two maps (what a prune has to write) */
export function mixChanged(before: IdentityMix, after: IdentityMix): string[] {
  const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
  return [...keys].filter((k) => (before[k] ?? []).join(',') !== (after[k] ?? []).join(','));
}

/** every agent, connection and key a personality is mixed into, in the map's order */
export function whereMixed(id: string, mix: IdentityMix): string[] {
  return Object.keys(mix).filter((k) => mix[k].includes(id));
}

/** an heir's list with the one it takes over laid after it: the heir's own order kept, what it lacked appended */
export function mergeMixLists(heir: readonly string[] | null | undefined, from: readonly string[] | null | undefined): string[] {
  return cleanMixIds([...(heir ?? []), ...(from ?? [])]);
}

/** a copy to make a variant of: new ids, "<name> copy", no offers (they stay waiting on the one they came to) */
export function duplicateIdentity(i: AgentIdentity, now: string): AgentIdentity {
  return {
    ...i,
    id: newId(),
    name: clip(`${i.name} copy`, 60),
    habits: i.habits.map((h) => ({ ...h, id: newId() })),
    suggestions: [],
    learnedAt: null,
    updatedAt: now,
  };
}

/**
 * "compose with…": a NEW personality the person can edit, made from the mix of
 * the ones they picked. Each field's words go into its free text as far as one
 * personality may write; what does not fit stays as kept lines of that field, so
 * nothing they mixed is lost; the habits follow. It is for no tool and not for every
 * agent (MIX_ONLY), and stands last in the list, so it serves nowhere until it is
 * chosen somewhere.
 */
export function materializeMix(chosen: readonly AgentIdentity[], now: string, name?: string): AgentIdentity | null {
  const mixed = composeIdentities(chosen);
  if (!mixed) return null;
  const out = mixOnlyIdentity(now, clip(name ?? mixed.name, 60) || 'a mix');
  const habits: IdentityHabit[] = [];
  const all = mixed.habits;
  for (const f of FIELDS) {
    const lines = [...sentencesOf(mixed[f]), ...all.filter((h) => h.field === f).flatMap((h) => sentencesOf(h.text))];
    const free: string[] = [];
    for (const s of lines) {
      if (joinLines([...free, s]).length <= MAX_FIELD) free.push(s);
      else habits.push({ id: newId(), text: clip(s, MAX_HABIT), field: f, from: 'you', on: true });
    }
    out[f] = joinLines(free);
  }
  for (const h of all) if (!h.field) habits.push({ id: newId(), text: clip(h.text, MAX_HABIT), from: 'you', on: true });
  out.habits = habits.slice(Math.max(0, habits.length - MAX_HABITS));
  return out;
}
